/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*
* Renix redesign (Phase 2): quiet accent selection — 8% accent fill + 3px left accent bar
* + accent icon/label — replaces the green gradient + glow. Neutral ink for resting rows.
*/
function collapseItem(theme, ownerState) {
  const { palette, transitions, breakpoints, borders, functions } = theme;
  const { active, miniSidenav } = ownerState;

  const { accent, ink, transparent } = palette;
  const { borderRadius } = borders;
  const { pxToRem, rgba } = functions;

  return {
    background: active ? accent.soft : transparent.main,
    color: active ? accent.onSurface : ink.main,
    display: "flex",
    alignItems: "center",
    // The horizontal gutter lives on the <List> (see examples/Sidenav/index.js), NOT here:
    // `width: 100%` + a horizontal margin made the row 2×gutter wider than the rail, which
    // pushed the icon-only content off-centre to the right (clipped by overflow: hidden).
    width: "100%",
    boxSizing: "border-box",
    padding: miniSidenav ? `${pxToRem(8)} 0` : `${pxToRem(8)} ${pxToRem(10)}`,
    margin: `${pxToRem(1.5)} 0`,
    justifyContent: miniSidenav ? "center" : "flex-start",
    borderRadius: borderRadius.sm,
    borderLeft: `${pxToRem(3)} solid ${active ? accent.main : "transparent"}`,
    // Mirror the accent bar in mini so the 3px it occupies does not push the icon off-centre.
    ...(miniSidenav && { borderRight: `${pxToRem(3)} solid transparent` }),
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
    boxShadow: "none",

    [breakpoints.up("navOverlay")]: {
      transition: transitions.create(["background-color", "color"], {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.shorter,
      }),
    },

    "&:hover, &:focus": {
      backgroundColor: active ? accent.soft : rgba(ink.main, 0.04),
    },
  };
}

function collapseIconBox(theme, ownerState) {
  const { palette, transitions, borders, functions } = theme;
  const { active } = ownerState;

  const { accent, ink } = palette;
  const { borderRadius } = borders;
  const { pxToRem } = functions;

  return {
    minWidth: pxToRem(32),
    minHeight: pxToRem(32),
    color: active ? accent.onSurface : ink.secondary,
    borderRadius: borderRadius.md,
    display: "grid",
    placeItems: "center",
    transition: transitions.create("margin", {
      easing: transitions.easing.easeInOut,
      duration: transitions.duration.standard,
    }),

    "& svg, svg g": {
      color: active ? accent.onSurface : ink.secondary,
    },
  };
}

const collapseIcon = ({ palette: { accent, ink } }, { active }) => ({
  color: active ? accent.onSurface : ink.secondary,
  fontSize: "20px !important",
});

function collapseText(theme, ownerState) {
  const { typography, transitions, breakpoints, functions } = theme;
  const { miniSidenav, active } = ownerState;

  const { size, fontWeightMedium, fontWeightRegular } = typography;
  const { pxToRem } = functions;

  return {
    marginLeft: pxToRem(10),

    [breakpoints.up("navOverlay")]: {
      opacity: miniSidenav ? 0 : 1,
      maxWidth: miniSidenav ? 0 : "100%",
      marginLeft: miniSidenav ? 0 : pxToRem(10),
      transition: transitions.create(["opacity", "margin"], {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.standard,
      }),
    },

    "& span": {
      fontWeight: active ? fontWeightMedium : fontWeightRegular,
      fontSize: size.sm,
      lineHeight: 0,
    },
  };
}

export { collapseItem, collapseIconBox, collapseIcon, collapseText };
